import axios from 'axios';

import type { CustomField } from 'bundles/apex/custom_fields/types';
import type { IBulkRowHook, IDeveloperField, IReviewStepData, IRowHookInput, IStepHook } from 'dromo-uploader-js';

type StepHookParams = Parameters<IStepHook['callback']>;
type Dromo = StepHookParams[0];
type StepHookData = StepHookParams[1];

type Query = string | number;
type IdsByQuery = Record<Query, number>;

interface Keys {
  idKey: string;
  queryKey: string;
  assignedToIdKey?: string;
}

interface HookOptions {
  // If a field is marked as required in Dromo but missing a mapping in an uploaded CSV, the user is
  // forced to manually add the field via the import UI.
  // However, fields added that way are not detectable programmatically.
  // Instead:
  //   * Leave such fields unrequired in Dromo fields
  //   * Set `force` to true when adding the hook
  // This will:
  //   * Add the ID field as a required field
  //   * Ensure the ID field is adjacent to the field used for the query in the UI
  //   * Not require the user to manually add the unmapped field after uploading
  force?: boolean;
}

const configsByType = {
  'contact': { maxQueriesPerRequest: 5000, path: '/crm/imports/find_contact_ids' },
  'deal': { maxQueriesPerRequest: 5000, path: '/crm/imports/find_deal_ids' },
  'property': { maxQueriesPerRequest: 1000, path: '/crm/imports/find_property_ids' },
  'user': { path: '/crm/imports/find_user_ids' },
};

export function buildContactIdLookupHooks(label: string, keys: Keys, options?: HookOptions) {
  return buildIdLookupHooks(label, keys, 'contact', options);
}

export function buildPropertyIdLookupHooks(label: string, keys: Keys, options?: HookOptions) {
  return buildIdLookupHooks(label, keys, 'property', options);
}

export function buildUserIdLookupHooks(label: string, keys: Keys, options?: HookOptions) {
  return buildIdLookupHooks(label, keys, 'user', options);
}

interface AssetTypeKeys {
  assetTypeKey: string;
  assetQueryKey: string;
  assetIdKey: string;
  assignedToIdKey?: string; // Optional key for assigned user ID (used for deal lookups)
}

export function buildDynamicAssetIdLookupHooks(label: string, assetKeys: AssetTypeKeys, options?: HookOptions): {
  stepHook: IStepHook;
  bulkRowHook: IBulkRowHook;
} {
  return {
    stepHook: {
      type: 'REVIEW_STEP',
      callback: (instance, data) => {
        const keys = { idKey: assetKeys.assetIdKey, queryKey: assetKeys.assetQueryKey };
        addIdField(instance, data, label, keys, options || {});
      },
    },
    bulkRowHook: (records: IRowHookInput[]) => {
      return loadDynamicAssetIds(records, assetKeys, options);
    },
  };
}

async function loadDynamicAssetIds(records: IRowHookInput[], assetKeys: AssetTypeKeys, options: HookOptions = {}) {
  if (!records.length || !Object.prototype.hasOwnProperty.call(records[0].row, assetKeys.assetIdKey)) {
    return records;
  }

  // Clear ID field and initialize info arrays when there's a query value
  // This ensures we do a fresh lookup when asset type or asset name changes
  for (const record of records) {
    const idCell = record.row[assetKeys.assetIdKey];
    const queryCell = record.row[assetKeys.assetQueryKey];

    if (idCell) {
      idCell.info = [];

      // Clear the ID if there's a query value, forcing a fresh lookup
      if (queryCell?.value) {
        idCell.value = null;
        idCell.resultValue = null;
      }
    }
  }

  // Group records by asset type
  const recordsByType = new Map<string, IRowHookInput[]>();
  for (const record of records) {
    const assetType = record.row[assetKeys.assetTypeKey]?.value?.toLowerCase();
    if (assetType && typeof assetType === 'string') {
      if (!recordsByType.has(assetType)) {
        recordsByType.set(assetType, []);
      }
      recordsByType.get(assetType)!.push(record);
    }
  }

  const lookupPromises = Array.from(recordsByType.entries()).map(async ([assetType, typeRecords]) => {
    if (!configsByType[assetType]) {
      // Mark records with unknown asset type as errors
      for (const record of typeRecords) {
        const idCell = record.row[assetKeys.assetIdKey];
        if (idCell) {
          idCell.info = [{
            level: 'error',
            message: `Unknown asset type: ${assetType}`,
          }];
        }
      }
      return;
    }

    const keys: Keys = { idKey: assetKeys.assetIdKey, queryKey: assetKeys.assetQueryKey };
    if ((assetType === 'deal' || assetType === 'contact') && assetKeys.assignedToIdKey) {
      keys.assignedToIdKey = assetKeys.assignedToIdKey;
    }

    return loadIds({ assetType, keys, options, records: typeRecords });
  });

  await Promise.all(lookupPromises);

  return records;
}

function buildIdLookupHooks(label: string, keys: Keys, assetType: string, options: HookOptions = {}): {
  stepHook: IStepHook;
  bulkRowHook: IBulkRowHook;
} {
  return {
    stepHook: {
      type: 'REVIEW_STEP',
      callback: (instance, data) => {
        addIdField(instance, data, label, keys, options);
      },
    },
    bulkRowHook: (records: IRowHookInput[]) => {
      return loadIds({ assetType, keys, options, records });
    },
  };
}

function addIdField(instance: Dromo, looselyTypedData: StepHookData, label: string, keys: Keys, options: HookOptions) {
  const data = looselyTypedData as IReviewStepData;
  if (!data.fields[keys.idKey] && (data.fields[keys.queryKey] || options.force)) {
    const field: IDeveloperField = {
      key: keys.idKey,
      label,
      type: ['number', 'integer'],
    };

    if (options.force) {
      field.validators = [{ validate: 'required' }];
    }

    // TODO: Could make this a dropdown, so the user can:
    //       * Choose an ID (might be multiple matches)
    //       * Choose none to create a new record
    instance.addField(field, { before: keys.queryKey });
  }
}

async function loadIds({ assetType, keys, options, records }: {
  assetType: string,
  keys: Keys,
  options: HookOptions,
  records: IRowHookInput[],
}) {
  if (!records.length || !Object.prototype.hasOwnProperty.call(records[0].row, keys.idKey)) {
    return records;
  }

  for (const record of records) {
    record.row[keys.idKey].info = [];
  }

  // If assignedToIdKey is provided, group by assigned user
  if (keys.assignedToIdKey) {
    const recordsByAssignee = new Map<number | null, IRowHookInput[]>();

    for (const record of records) {
      const assignedToId = record.row[keys.assignedToIdKey]?.resultValue || null;
      if (!recordsByAssignee.has(assignedToId)) {
        recordsByAssignee.set(assignedToId, []);
      }
      recordsByAssignee.get(assignedToId)!.push(record);
    }

    // Make separate calls for each assignee
    const assigneeLookups = Array.from(recordsByAssignee.entries()).map(async ([assignedToId, assigneeRecords]) => {
      const params = { assigned_to_id: assignedToId };
      return executeQueries({ assetType, keys, options, params, records: assigneeRecords });
    });

    await Promise.all(assigneeLookups);
  } else {
    // Standard lookup without assignedToId grouping
    await executeQueries({ assetType, keys, options, records });
  }

  return records;
}

async function executeQueries({ assetType, keys, options = {}, params = {}, records }: {
  assetType: string;
  keys: Keys;
  options?: HookOptions;
  params?: { assigned_to_id?: number | null };
  records: IRowHookInput[];
}) {
  const config = configsByType[assetType];
  if (!config) {
    return;
  }

  const queries = getQueries(records, keys);
  if (!queries.length) {
    return;
  }

  const slices: Query[][] = [];
  if (config.maxQueriesPerRequest) {
    for (let i = 0; i < queries.length; i += config.maxQueriesPerRequest) {
      slices.push(queries.slice(i, i + config.maxQueriesPerRequest));
    }
  } else {
    slices.push(queries);
  }

  const idsByQuery: Record<Query, number> = {};

  await Promise.all(slices.map(async (slice) => {
    try {
      const response = await axios.post(config.path, { queries: slice, ...params });
      Object.assign(idsByQuery, response.data);
    } catch {
      for (const query of slice) {
        idsByQuery[query] = -1;
      }
    }
  }));

  for (const record of records) {
    setId(record, idsByQuery, keys, options, assetType);
  }
}

function getQueries(records: IRowHookInput[], keys: Keys): Query[] {
  const queries = new Set<string | number>();
  for (const record of records) {
    const query = record.row[keys.queryKey]?.value;
    const existingId = record.row[keys.idKey]?.resultValue;
    if (existingId) {
      queries.add(existingId);
    } else if (query) {
      queries.add(query);
    }
  }

  return [...queries];
}

function setId(record: IRowHookInput, idsByQuery: IdsByQuery, keys: Keys, options: HookOptions, assetType: string) {
  const idCell = record.row[keys.idKey];
  const existingId = idCell?.resultValue || null;

  let foundId: number;
  if (existingId) {
    foundId = idsByQuery[existingId];
  } else {
    const queryCell = record.row[keys.queryKey];
    if (!queryCell) {
      return;
    }

    foundId = idsByQuery[queryCell?.value];
  }

  if (foundId === -1) {
    idCell.info = [{
      level: 'error',
      message: `Failed to lookup ${assetType}`,
    }];
    return;
  }

  idCell.value = idCell.resultValue = foundId || existingId;

  if (!foundId) {
    idCell.info = [{
      level: options.force ? 'error' : 'warning',
      message: 'Not found',
    }];
  }
}

export function mapCustomFieldsToDromoFields(customFields: CustomField[]): IDeveloperField[] {
  return customFields.map((customField) => {
    let options, type;
    if (customField.type === 'currency') {
      type = ['number', 'usd'];
    } else if (customField.type === 'date') {
      type = ['date', { displayFormat: 'MM/dd/yyyy' }];
    } else if (customField.type === 'float' || customField.type === 'number') {
      type = 'number';
    } else if (customField.type === 'percentage') {
      type = ['number', 'percent'];
    } else if (customField.options?.length) {
      type = 'select';
      options = customField.options.map(option => ({ label: option, value: option }));
    } else {
      type = 'string';
    }

    return {
      key: customField.name,
      label: customField.label,
      selectOptions: options,
      type,
    };
  });
}

export function addDefaultValidators(fields: IDeveloperField[]) {
  for (const field of fields) {
    field.validators = field.validators || [];

    if (field.type === 'string' && !field.validators.find(validator => validator.validate === 'length')) {
      field.validators.push({ validate: 'length', max: 255 });
    }
  }

  return fields;
}
